import React, { useState, useRef, useEffect } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { setVisibility, setHeight } from '../../../../redux/slices/panelSlice';
import InstanceViewerWithTree from '../customer_node_definitions/node_definition_editor/instance_tree/InstanceViewerWithTree';
import { ReactFlowProvider } from 'reactflow';
import { useAppContext } from 'providers/AppProvider';
import Splitter from 'components/Splitter/Splitter';

// Minimum height the table keeps above the panel when it's dragged taller.
const MIN_TABLE_HEIGHT = 120;

const ResizableBottomPanel: React.FC = () => {
  const panelHeight = useAppSelector((state) => state.bottomPanel.height);
  const [localHeight, setLocalHeight] = useState<number>(panelHeight);
  const [maxHeight, setMaxHeight] = useState<number>(panelHeight);
  const panelRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const {
    config: { theme },
  } = useAppContext();

  const isVisible = useAppSelector((state) => state.bottomPanel.isVisible);
  const nodeInstanceId = useAppSelector((state) => state.bottomPanel.node_instance_id);

  // Auto toggle visibility based on route
  useEffect(() => {
    if (location.pathname !== '/automation/node_instances') {
      dispatch(setVisibility(false));
    }
  }, [location.pathname, dispatch]);

  // The panel shares the table shell's height; cap its size so the table keeps
  // a minimum. Measure the shell when shown and on viewport resize. The panel only
  // renders (and panelRef only attaches) once it has an instance id as well, so the
  // id is a dependency too.
  useEffect(() => {
    const update = () => {
      const parent = panelRef.current?.parentElement;
      if (parent) {
        setMaxHeight(Math.max(150, parent.clientHeight - MIN_TABLE_HEIGHT));
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isVisible, nodeInstanceId]);

  const handleResize = (height: number) => {
    setLocalHeight(height);
    dispatch(setHeight(height));
  };

  const handleClose = () => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.delete('bottom_panel_id');
    const newSearch = currentParams.toString();
    navigate(newSearch ? `?${newSearch}` : location.pathname, {
      replace: true,
    });
  };

  // The panel is only ever shown with an instance: nodeInstances sets the id before the
  // visibility, and clearPanel resets both.
  if (!isVisible || nodeInstanceId === null) return null;

  return (
    <>
      <Splitter orientation="horizontal" side="after" size={localHeight} min={100} max={maxHeight} onResize={handleResize} ariaLabel="Resize instance panel" />
      <div
        ref={panelRef}
        data-polling-lock="true"
        style={{
          flex: `0 0 ${localHeight}px`,
          minHeight: 0,
          background: theme === 'dark' ? 'rgb(20, 24, 36)' : '#fff',
          borderTop: '1px solid #ccc',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '8px',
            borderBottom: '1px solid #ccc',
            display: 'flex',
            justifyContent: 'flex-start',
            flex: '0 0 auto',
          }}
        >
          <button
            onClick={handleClose}
            style={{
              background: theme === 'dark' ? 'rgb(20, 24, 36)' : 'white',
              border: theme === 'dark' ? '1px solid white' : '1px solid #aaa',
              color: theme === 'dark' ? 'white' : 'black',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <ReactFlowProvider>
            <InstanceViewerWithTree key={nodeInstanceId} entryId={nodeInstanceId} />
          </ReactFlowProvider>
        </div>
      </div>
    </>
  );
};

export default ResizableBottomPanel;
